import React, { useEffect, useState } from 'react';
import { Users, Copy, UserPlus, Heart, X, Send, Sparkles, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { CHARACTERS } from '../lib/levels';

const REACT_EMOJIS = ['❤️', '🎉', '🌟', '💪', '🔥', '🧠', '🚀', '👏'];

const CharacterAvatar = ({ character, size = 12 }) => {
  const c = CHARACTERS[character] || CHARACTERS.ouistiti;
  const sz = { 10: 'w-10 h-10', 12: 'w-12 h-12', 16: 'w-16 h-16' }[size] || 'w-12 h-12';
  return (
    <div className={`${sz} rounded-full overflow-hidden border-2 border-gold bg-cream shrink-0`}>
      <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
    </div>
  );
};

const ReactModal = ({ buddy, onClose, onSent }) => {
  const [emoji, setEmoji] = useState('❤️');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/buddies/${buddy.id}/react`, { emoji, message: message.trim() || null });
      toast.success(`Sent ${emoji} to ${buddy.buddy_name}!`);
      onSent(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-navy/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="react-modal">
      <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-sm p-5 shadow-squishyLg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <CharacterAvatar character={buddy.buddy_character} size={10} />
            <div>
              <div className="text-[10px] font-heading font-bold text-teal uppercase tracking-wider">Send a high-five</div>
              <div className="font-heading font-bold text-navy">{buddy.buddy_name}</div>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-cream flex items-center justify-center" data-testid="react-close"><X className="w-4 h-4 text-navy" /></button>
        </div>
        <div className="grid grid-cols-4 gap-2 my-3" data-testid="emoji-grid">
          {REACT_EMOJIS.map((e) => (
            <button key={e} onClick={() => setEmoji(e)} data-testid={`emoji-${e}`}
              className={`aspect-square rounded-2xl text-3xl transition-all ${emoji === e ? 'bg-gold/30 border-2 border-gold scale-110' : 'bg-cream hover:bg-gold/15'}`}>{e}</button>
          ))}
        </div>
        <input value={message} onChange={(e) => setMessage(e.target.value)} maxLength={200}
          placeholder="Add a short note (optional)"
          className="w-full h-11 px-3 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none font-body text-sm"
          data-testid="react-message" />
        <button onClick={send} disabled={busy}
          className="mt-3 w-full h-12 rounded-full bg-gold text-navy font-heading font-bold shadow-gold disabled:opacity-60 inline-flex items-center justify-center gap-2"
          data-testid="react-send">
          <Send className="w-4 h-4" /> {busy ? 'Sending…' : `Send ${emoji}`}
        </button>
      </div>
    </div>
  );
};

export default function Buddies() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addCode, setAddCode] = useState('');
  const [adding, setAdding] = useState(false);
  const [reactTarget, setReactTarget] = useState(null);

  const load = async () => {
    setError(null);
    try {
      const { data } = await api.get('/buddies/me');
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load buddies');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const copyCode = async () => {
    if (!data?.code) return;
    try {
      await navigator.clipboard.writeText(data.code);
      toast.success(`Code ${data.code} copied — send it to your friend!`);
    } catch { toast.error('Copy failed'); }
  };

  const addByCode = async (e) => {
    e.preventDefault();
    const code = addCode.trim().toUpperCase();
    if (!code) return;
    setAdding(true);
    try {
      const { data: res } = await api.post('/buddies/add', { code });
      toast.success(res.message || 'Buddy request sent!');
      setAddCode('');
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to add buddy'); }
    finally { setAdding(false); }
  };

  const remove = async (buddyId, name) => {
    if (!window.confirm(`Remove ${name} as a buddy?`)) return;
    try {
      await api.delete(`/buddies/${buddyId}`);
      toast.success('Buddy removed');
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="text-center py-8 text-navy/60 font-heading" data-testid="buddies-loading">Loading buddies…</div>;
  if (error) return (
    <div className="bg-white rounded-3xl p-6 border-2 border-red-200 text-center" data-testid="buddies-error">
      <div className="text-navy/70 text-sm mb-3">{error}</div>
      <button onClick={() => { setLoading(true); load(); }} className="h-10 px-5 rounded-full bg-navy text-white font-heading font-bold text-sm" data-testid="buddies-retry">Retry</button>
    </div>
  );
  if (!data) return null;

  const accepted = (data.buddies || []).filter((b) => b.status === 'accepted');
  const pending = (data.buddies || []).filter((b) => b.status === 'pending_parent_approval');

  return (
    <div className="space-y-5" data-testid="buddies-page">
      {/* Weekly challenge */}
      {data.challenge && (
        <section className="bg-gradient-to-br from-gold via-teal to-navy rounded-3xl p-5 text-white shadow-squishyLg border-4 border-gold" data-testid="buddy-challenge">
          <div className="flex items-start gap-3">
            <div className="text-4xl">{data.challenge.emoji}</div>
            <div className="min-w-0 flex-1">
              <div className="text-gold font-heading font-bold text-[10px] uppercase tracking-wider">
                <Trophy className="w-3 h-3 inline mr-1" /> This Week's Buddy Challenge
              </div>
              <div className="font-heading text-lg font-bold mt-0.5">{data.challenge.title}</div>
              <div className="text-white/85 text-sm mt-1">{data.challenge.desc}</div>
            </div>
          </div>
        </section>
      )}

      {/* Your buddy code */}
      <section className="bg-white rounded-3xl p-5 shadow-squishy border-2 border-cream" data-testid="buddy-code-card">
        <div className="flex items-center gap-2 mb-2">
          <Users className="w-4 h-4 text-teal" />
          <span className="text-teal font-heading font-bold text-[11px] uppercase tracking-wider">Your Buddy Code</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1 font-heading text-3xl font-bold text-navy tracking-widest bg-cream rounded-2xl py-3 text-center border-2 border-dashed border-teal" data-testid="my-buddy-code">{data.code}</div>
          <button onClick={copyCode} className="h-14 w-14 rounded-2xl bg-teal text-white flex items-center justify-center shadow-squishy hover:scale-105" data-testid="copy-buddy-code" aria-label="Copy code">
            <Copy className="w-5 h-5" />
          </button>
        </div>
        <p className="text-navy/60 text-xs mt-2">Share this code with a friend so they can add you as a buddy. Parent approval is required if either of you is under 13.</p>
      </section>

      {/* Add a buddy */}
      <section className="bg-white rounded-3xl p-5 shadow-squishy border-2 border-cream" data-testid="add-buddy-card">
        <div className="flex items-center gap-2 mb-2">
          <UserPlus className="w-4 h-4 text-gold" />
          <span className="text-gold font-heading font-bold text-[11px] uppercase tracking-wider">Add A Buddy</span>
        </div>
        <form onSubmit={addByCode} className="flex gap-2">
          <input value={addCode} onChange={(e) => setAddCode(e.target.value.toUpperCase())} maxLength={12}
            placeholder="Enter buddy code (e.g. K7X9B2)"
            className="flex-1 h-12 px-3 rounded-xl border-2 border-slate-200 focus:border-teal focus:outline-none font-body text-navy uppercase tracking-widest"
            data-testid="add-buddy-code-input" />
          <button type="submit" disabled={adding || !addCode.trim()}
            className="h-12 px-5 rounded-xl bg-gold text-navy font-heading font-bold shadow-gold disabled:opacity-60"
            data-testid="add-buddy-submit">
            {adding ? '…' : 'Add'}
          </button>
        </form>
      </section>

      {/* Buddies list */}
      <section className="bg-white rounded-3xl p-5 shadow-squishy border-2 border-cream" data-testid="my-buddies-list">
        <div className="flex items-center gap-2 mb-3">
          <Heart className="w-4 h-4 text-teal" />
          <span className="text-teal font-heading font-bold text-[11px] uppercase tracking-wider">My Buddies</span>
          <span className="ml-auto text-navy/50 text-xs font-bold">{accepted.length} active</span>
        </div>
        {accepted.length === 0 && pending.length === 0 && (
          <p className="text-navy/60 text-sm text-center py-6" data-testid="buddies-empty">No buddies yet. Share your code above to team up with a friend!</p>
        )}
        <div className="space-y-2">
          {accepted.map((b) => (
            <div key={b.id} className="flex items-center gap-2 p-3 rounded-2xl bg-cream border-2 border-cream flex-wrap" data-testid={`buddy-item-${b.id}`}>
              <CharacterAvatar character={b.buddy_character} size={10} />
              <div className="flex-1 min-w-[6rem]">
                <div className="font-heading font-bold text-navy break-words">{b.buddy_name}</div>
                <div className="text-teal text-[11px] font-bold">Active buddy ✓</div>
              </div>
              <button onClick={() => setReactTarget(b)}
                className="h-10 px-3 rounded-full bg-gold text-navy font-heading font-bold text-xs shadow-gold hover:scale-105 transition-transform whitespace-nowrap"
                data-testid={`send-react-${b.id}`}>
                <Sparkles className="w-3.5 h-3.5 inline mr-1" /> High-five
              </button>
              <button onClick={() => remove(b.id, b.buddy_name)}
                className="w-8 h-8 rounded-full bg-white text-navy/60 flex items-center justify-center hover:bg-red-50 hover:text-red-500 shrink-0"
                data-testid={`remove-buddy-${b.id}`} aria-label="Remove buddy"><X className="w-4 h-4" /></button>
            </div>
          ))}
          {pending.map((b) => (
            <div key={b.id} className="flex items-center gap-3 p-3 rounded-2xl bg-white border-2 border-dashed border-gold" data-testid={`buddy-pending-${b.id}`}>
              <CharacterAvatar character={b.buddy_character} size={10} />
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold text-navy truncate">{b.buddy_name}</div>
                <div className="text-gold text-[11px] font-bold">⏳ Waiting for parent approval</div>
              </div>
              <button onClick={() => remove(b.id, b.buddy_name)}
                className="text-navy/50 text-xs underline"
                data-testid={`cancel-buddy-${b.id}`}>Cancel</button>
            </div>
          ))}
        </div>
      </section>

      {/* Reactions inbox */}
      <section className="bg-white rounded-3xl p-5 shadow-squishy border-2 border-cream" data-testid="reactions-inbox">
        <div className="flex items-center gap-2 mb-3">
          <Heart className="w-4 h-4 text-gold" />
          <span className="text-gold font-heading font-bold text-[11px] uppercase tracking-wider">High-fives Received</span>
        </div>
        {(data.reactions || []).length === 0 ? (
          <p className="text-navy/60 text-sm text-center py-4" data-testid="reactions-empty">Nothing yet — send a high-five and one might come back!</p>
        ) : (
          <div className="space-y-2">
            {data.reactions.map((r) => (
              <div key={r.id} className="flex items-center gap-3 p-2.5 rounded-2xl bg-cream border-2 border-cream" data-testid={`reaction-${r.id}`}>
                <div className="text-2xl">{r.emoji}</div>
                <div className="min-w-0 flex-1">
                  <div className="font-heading font-bold text-navy text-sm truncate">from {r.from_user_name}</div>
                  {r.message && <div className="text-navy/70 text-xs italic truncate">"{r.message}"</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {reactTarget && <ReactModal buddy={reactTarget} onClose={() => setReactTarget(null)} onSent={load} />}
    </div>
  );
}
